import _ from 'lodash';
import { Epic, combineEpics } from 'redux-observable';
import {
    MAKE_OFFER,
    DECLINE_BOOKING,
    SchoolBookingAction,
    WITHDRAW_OFFER,
    IMPORT_BOOKING,
    ADD_INVOICE,
    MARK_INVOICE_PAID
} from '../actions/SchoolBookingActions';
import * as api from '../api';
import { StoreState, RequestForm, OfferForm, BookingInvoiceItem, BOOKING_STATUS_CONFIRMED } from '../types';
import { createTryActionsEpic } from './Common';
import { createRequestId } from '../api/Commands';
import { SEND_SCHOOL_BOOKING_MESSAGE } from '../actions/BookingActions';

const makeOffer = async (bookingId: string, schoolId: string, requestId: string, { auth, schoolBooking }: StoreState) => {
    const user = auth.user;
    if (!user || !user.uid) {
        throw new Error('Tried to make an offer without authenticating');
    }

    const bookings = schoolBooking.bookings[schoolId];
    if (!bookings) {
        throw new Error(`School bookings for ${schoolId} not found`);
    }

    const booking = bookings[bookingId];
    if (!booking) {
        throw new Error(`Booking ${bookingId} not found for school ${schoolId}`);
    }

    const request = _.find(booking.requests, r => r.requestId === requestId);
    if (!request) {
        throw new Error(`Request ${requestId} for not found for school ${schoolId}`);
    }

    const school = _.find(schoolBooking.schools, s => s.id === schoolId);
    if (!school) {
        throw new Error(`School ${schoolId} not found`);
    }

    const commissionModel = booking.commissionModel;
    if (!commissionModel) {
        throw new Error(`commissionModel for ${schoolId} not found`);
    }
    if (!commissionModel.currency) {
        throw new Error(`commissionModel currency for ${schoolId} not found`);
    }

    return await api.makeOffer({
        ...booking.offerForm,
        currency: commissionModel.currency,
        bookingId,
        schoolId,
        requestId
    });
};

const importBooking = async (schoolId: string, request: RequestForm, offer: OfferForm) => {
    if (request.group.length === 0) {
        throw new Error(`Attempted to import booking with no people`);
    }

    const firstPerson = request.group[0];

    if (!firstPerson.name) {
        throw new Error(`Attempted to import booking with no booking name`);
    }

    return await api.importBooking({
        bookingEmail: firstPerson.email,
        bookingName: firstPerson.name,
        bookingTel: firstPerson.tel,
        offer: offer,
        schoolId: schoolId,
        status: BOOKING_STATUS_CONFIRMED,
        requests: [
            {
                ...request,
                requestId: createRequestId(schoolId, request.activity || ''),
                group: request.group.filter(m => !!m.name),
                schoolId: schoolId,
            }
        ]
    });
};

const addInvoice = async (bookingId: string, schoolId: string, items: BookingInvoiceItem[], { schoolBooking }: StoreState) => {
    const bookings = schoolBooking.bookings[schoolId];
    if (!bookings) {
        throw new Error(`School bookings for ${schoolId} not found`);
    }

    const booking = bookings[bookingId];
    if (!booking) {
        throw new Error(`Booking ${bookingId} not found for school ${schoolId}`);
    }

    const commissionModel = booking.commissionModel;
    if (!commissionModel) {
        throw new Error(`commissionModel for ${schoolId} not found`);
    }
    if (!commissionModel.currency) {
        throw new Error(`commissionModel currency for ${schoolId} not found`);
    }

    if (items.length === 0) {
        throw new Error(`No invoice items for ${schoolId}`);
    }

    return await api.addInvoice({
        bookingId: bookingId,
        schoolId: schoolId,
        currency: commissionModel.currency,
        items: items
    });
};

const markInvoicePaid = async (bookingId: string, schoolId: string, invoiceId: string) => {
    return await api.markInvoicePaid({
        bookingId: bookingId,
        schoolId: schoolId,
        invoiceId: invoiceId
    });
};

const declineBooking = async (bookingId: string, schoolId: string, { auth, schoolBooking }: StoreState) => {
    const user = auth.user;
    if (!user || !user.uid) {
        throw new Error('Tried to decline a booking without authenticating');
    }

    const bookings = schoolBooking.bookings[schoolId];
    if (!bookings) {
        throw new Error(`School bookings for ${schoolId} not found`);
    }

    const booking = bookings[bookingId];
    if (!booking) {
        throw new Error(`Booking ${bookingId} not found for school ${schoolId}`);
    }

    return await api.declineBooking({
        ...booking.declineForm,
        bookingId: bookingId,
        schoolId: schoolId
    });
};

const withdrawOffer = async (bookingId: string, schoolId: string, requestId: string, offerId: string, { auth, schoolBooking }: StoreState) => {
    const user = auth.user;
    if (!user || !user.uid) {
        throw new Error('Tried to withdraw an offer without authenticating');
    }

    const bookings = schoolBooking.bookings[schoolId];
    if (!bookings) {
        throw new Error(`School bookings for ${schoolId} not found`);
    }

    const booking = bookings[bookingId];
    if (!booking) {
        throw new Error(`Booking ${bookingId} not found for school ${schoolId}`);
    }

    const request = _.find(booking.requests, r => r.requestId === requestId);
    if (!request) {
        throw new Error(`Request ${requestId} not found for school ${schoolId}`);
    }

    const offer = _.find(request.offers, o => o.offerId === offerId);
    if (!offer) {
        throw new Error(`Offer ${offerId} not found for school ${schoolId}`);
    }

    return await api.withdrawOffer({
        bookingId,
        offerId,
        userId: user.uid,
        schoolId,
        requestId
    });
};

const sendSchoolBookingMessage = async (sendBookingMessage: { bookingId: string; schoolId: string; message: string; messageId: string; isSchool: boolean }, { auth }: StoreState) => {
    const user = auth.user;
    if (!user || !user.uid) {
        throw new Error('Tried to send a school booking message when not authenticated');
    }

    await api.sendBookingMessage(sendBookingMessage);
};

const makeOfferEpic: Epic<SchoolBookingAction, SchoolBookingAction, StoreState> =
    createTryActionsEpic(
        MAKE_OFFER,
        (a, s) => makeOffer(a.value.bookingId, a.value.schoolId, a.value.requestId, s));

const declineBookingEpic: Epic<SchoolBookingAction, SchoolBookingAction, StoreState> =
    createTryActionsEpic(
        DECLINE_BOOKING,
        (a, s) => declineBooking(a.value.bookingId, a.value.schoolId, s),
        (a) => a.value);

const withdrawOfferEpic: Epic<SchoolBookingAction, SchoolBookingAction, StoreState> =
    createTryActionsEpic(
        WITHDRAW_OFFER,
        (a, s) => withdrawOffer(a.value.bookingId, a.value.schoolId, a.value.requestId, a.value.offerId, s),
        (a) => a.value);

const importBookingEpic: Epic<SchoolBookingAction, SchoolBookingAction, StoreState> =
    createTryActionsEpic(
        IMPORT_BOOKING,
        (a) => importBooking(a.value.schoolId, a.value.request, a.value.offer),
        (a) => a.value);

const addInvoiceEpic: Epic<SchoolBookingAction, SchoolBookingAction, StoreState> =
    createTryActionsEpic(
        ADD_INVOICE,
        (a, s) => addInvoice(a.value.bookingId, a.value.schoolId, a.value.items, s),
        (a) => a.value);

const markInvoicePaidEpic: Epic<SchoolBookingAction, SchoolBookingAction, StoreState> =
    createTryActionsEpic(
        MARK_INVOICE_PAID,
        (a) => markInvoicePaid(a.value.bookingId, a.value.schoolId, a.value.invoiceId),
        (a) => a.value);

const sendSchoolBookingMessageEpic: Epic<SchoolBookingAction, SchoolBookingAction, StoreState> =
    createTryActionsEpic(
        SEND_SCHOOL_BOOKING_MESSAGE,
        (a, s) => sendSchoolBookingMessage(a.value, s),
        (a) => a.value);

export default combineEpics<SchoolBookingAction, SchoolBookingAction, StoreState>(
    makeOfferEpic,
    declineBookingEpic,
    withdrawOfferEpic,
    importBookingEpic,
    addInvoiceEpic,
    markInvoicePaidEpic,
    sendSchoolBookingMessageEpic
);
